import React, { memo, ReactNode, useCallback, useRef, useState } from 'react';
import { DOMProps } from '@react-types/shared';
import { useLogger } from '@direct-frontend/modules-logger';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';
import './MobileSwiper.css';
import { VStack, HStack } from '../Stack';
import { BaseSliderAnimation } from '../BaseSliderAnimation';

const cls = cn('MobileSwiper');

function getNextItemIndex(currentIndex: number, itemsCount: number, loop: boolean) {
    if (currentIndex < itemsCount - 1) {
        return currentIndex + 1;
    }
    return loop ? 0 : currentIndex;
}

function getPrevItemIndex(currentIndex: number, itemsCount: number, loop: boolean) {
    if (currentIndex > 0) {
        return currentIndex - 1;
    }
    return loop ? itemsCount - 1 : currentIndex;
}

export interface MobileSwiperProps extends TestProps, DOMProps {
    /**
     * Длительность анимации свайпа
     */
    animationDuration?: number;
    /**
     * Массив нод, между которыми нужно переключаться
     */
    items: ReactNode[];

    /**
     * Колбэк на изменнение выбранного элемента
     * @param selectedItemIndex
     */
    onSelectedItemChange?: (selectedItemIndex: number) => void;

    /**
     * Дополнительный класс для основного элемента
     */
    className?: string;
    /**
     * Цвет индикатора, который показывает в каком положении находится свайпер
     * @default 'normal'
     */
    indicatorColor?: 'normal' | 'contrast' | 'current';

    /**
     * Дополнительный класс для индикатора
     */
    indicatorClassName?: string;

    /**
     * Должны ли показываться края соседних элементов.
     * При включении контейнер выходит за пределы острова, добавляет элементам gap=8px и отключает цикличность листания.
     * Использовать только внутри компонента Island
     */
    withOverflow?: boolean;
}

const MOBILE_SWIPE_DISTANCE = 50;

/**
 * Мобильная галерея, которая принимает на вход набор ReactNode и
 * умеет по свайпу прокручивать их вправо/влево с анимацией
 * Внизу галереи рисуется индикатор из точек, указывающий на то, какой элемент выбран
 * @deprecated Используйте GalleryWithPaginator
 */
export const MobileSwiper = memo<MobileSwiperProps>(function MobileSwiper(props) {
    const {
        animationDuration,
        items,
        className,
        indicatorColor = 'normal',
        'data-testid': dataTestId = 'MobileSwiper',
        onSelectedItemChange,
        indicatorClassName,
        withOverflow,
    } = props;
    const { LogProvider } = useLogger('MobileSwiper');
    const [selectedItemIndex, setSelectedItemIndex] = useState(0);
    const touchStartValue = useRef<number | undefined>(undefined);
    const touchEndValue = useRef<number | undefined>(undefined);
    const itemsCount = items.length;

    const hasManyItems = itemsCount > 1;

    const onTouchStart = useCallback(
        (event: React.TouchEvent<HTMLElement>) => {
            if (!hasManyItems) {
                return;
            }
            touchEndValue.current = undefined;
            touchStartValue.current = event.targetTouches[0]?.clientX;
        },
        [hasManyItems],
    );

    const onTouchMove = useCallback(
        (event: React.TouchEvent<HTMLElement>) => {
            if (!hasManyItems) {
                return;
            }
            touchEndValue.current = event.targetTouches[0]?.clientX;
        },
        [hasManyItems],
    );

    const onTouchEnd = useCallback(() => {
        if (!hasManyItems) {
            return;
        }

        if (touchStartValue.current === undefined || touchEndValue.current === undefined) return;
        const distance = touchStartValue.current - touchEndValue.current;
        const isLeftSwipe = distance > MOBILE_SWIPE_DISTANCE;
        const isRightSwipe = distance < -MOBILE_SWIPE_DISTANCE;

        if (isLeftSwipe) {
            const nextIndex = getNextItemIndex(selectedItemIndex, itemsCount, !withOverflow);
            setSelectedItemIndex(nextIndex);
            onSelectedItemChange && onSelectedItemChange(nextIndex);
        }

        if (isRightSwipe) {
            const prevIndex = getPrevItemIndex(selectedItemIndex, itemsCount, !withOverflow);
            setSelectedItemIndex(prevIndex);
            onSelectedItemChange && onSelectedItemChange(prevIndex);
        }
    }, [hasManyItems, selectedItemIndex, itemsCount, withOverflow, onSelectedItemChange]);

    return (
        <LogProvider>
            <div
                className={cls(
                    {
                        'indicator-color': indicatorColor,
                    },
                    [className],
                )}
                data-testid={dataTestId}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <VStack>
                    <BaseSliderAnimation
                        animationDuration={animationDuration}
                        className={cls('gallery', { 'with-overflow': withOverflow })}
                        data-testid={`${dataTestId}.Animation`}
                        selectedItemIndex={selectedItemIndex}
                        items={items}
                        gap={withOverflow ? '8' : undefined}
                    />
                    {hasManyItems && (
                        <HStack
                            gap="8"
                            data-testid={`${dataTestId}.Controls`}
                            justifyContent="center"
                            className={cls('indicator', [indicatorClassName])}
                        >
                            {items.map((item, index) => {
                                const dotDataTestId = `${dataTestId}.Dot${index}${
                                    index === selectedItemIndex ? '.Selected' : ''
                                }`;

                                return (
                                    <div className={cls('indicator-dot-container')} key={`indicator-dot-${index}`}>
                                        <div
                                            data-testid={dotDataTestId}
                                            className={cls('indicator-dot', { selected: index === selectedItemIndex })}
                                        />
                                    </div>
                                );
                            })}
                        </HStack>
                    )}
                </VStack>
            </div>
        </LogProvider>
    );
});
